/**
 * Feasibility section fallbacks (SNT-2).
 *
 * Pure deterministic 1-3 sentence fallbacks for the free-text string
 * fields on the feasibility schema. Each fallback returns a data-grounded
 * statement composed from `site` + `sed` only — no AI calls, no I/O.
 *
 * Contract:
 *   1. Pure: same `site`/`sed` → same string. No timestamps, no Math.random.
 *   2. Data-grounded only: NEVER use "should" / "must" / "is expected to" /
 *      "Reference X" / "Note any Y" — those trip the instruction-shape
 *      regex (recursion guard R6 in the SNT pack). Pinned by
 *      `instructionShapeGuard.test.ts`.
 *   3. Always returns a non-empty string. The "pending re-run" phrase is
 *      load-bearing UX — it tells the reader this is a fallback, not the
 *      final answer. The accompanying amber banner (SNT-3) reinforces it.
 *
 * Fires when the SNT-1 guard quarantines a section, OR when the model
 * returned empty/null for the field (the legacy OR-fallback case at
 * `siteRagService.ts:1981` collapses into this).
 */
import type { SiteEntity } from '../../types/site';
import type { SiteEvidenceDocument } from '../../types/siteEvidence';
import { formatVerifiedGridSummary } from '../gridClaimHonesty';
import {
  formatAttributedInternetExchangeFromSed,
  formatAttributedDatacenterFacilities,
  resolveEvidenceSourceLabel,
  isVerifiedEvidenceSection,
} from '../sourceAttribution';
import { composeClimateSummaryFromSed } from '../climateSevereWeatherNarrative';

/** Helper: derive a stable per-parcel acreage string. Both SED + Site may
 * carry the value (MP pack writes both); SED wins because the SED is the
 * gen-time snapshot and matches the rest of the report. */
function acreageLabel(site: SiteEntity, sed: SiteEvidenceDocument): string | null {
  const sedAcres = sed.siteProfile?.data?.acres;
  if (typeof sedAcres === 'number' && Number.isFinite(sedAcres) && sedAcres > 0) {
    return `${Math.round(sedAcres)} acres`;
  }
  const siteAcres = site.siteArea;
  if (typeof siteAcres === 'number' && Number.isFinite(siteAcres) && siteAcres > 0) {
    return `${Math.round(siteAcres)} acres`;
  }
  return null;
}

/** Helper: human-readable elevation label routed through the EL2 guard
 * (null / NaN → "pending"; never silently zero-coerces). */
function elevationLabel(sed: SiteEvidenceDocument): string | null {
  const elev = sed.siteProfile?.data?.elevationAvg;
  if (elev == null || !Number.isFinite(elev)) return null;
  if (elev === 0) return null; // MV5 — treat exact-zero as being verified.
  return `average elevation ${Math.round(elev)} m`;
}

/**
 * 2-3 sentence summary: site name + acreage + a neutral provenance note.
 *
 * Deliberately NOT a "verdict" — the deterministic scorer owns that. The
 * fallback only fires for executive-summary INSTRUCTION leaks; for
 * data-empty cases the existing OR-fallback at siteRagService.ts:1975
 * remains canonical.
 */
export function executiveSummaryFallback(site: SiteEntity, sed: SiteEvidenceDocument): string {
  const parts: string[] = [];
  const acres = acreageLabel(site, sed);
  const elev = elevationLabel(sed);
  const addr = sed.location?.address ?? site.address ?? null;

  if (acres && addr) parts.push(`${site.name} sits at ${addr} on ${acres}.`);
  else if (acres) parts.push(`${site.name} occupies ${acres}.`);
  else if (addr) parts.push(`${site.name} sits at ${addr}.`);
  else parts.push(`${site.name} site summary.`);

  if (elev) parts.push(`Site profile reports ${elev} above sea level.`);

  parts.push('Detailed narrative pending re-run.');
  return parts.join(' ');
}

/**
 * 1-3 sentence siting characteristics composed from FEMA flood + parcel +
 * site-profile evidence. This is the fallback that fires when the model
 * emits the "The Siting Notes should cite ..." leak.
 */
export function sitingNotesFallback(_site: SiteEntity, sed: SiteEvidenceDocument): string {
  const parts: string[] = [];
  const fz = sed.flood?.data?.floodZone;
  if (fz && typeof fz === 'string' && fz.length > 0) {
    parts.push(`FEMA flood zone ${fz}`);
  }
  const elev = sed.siteProfile?.data?.elevationAvg;
  if (typeof elev === 'number' && Number.isFinite(elev) && elev !== 0) {
    parts.push(`average elevation ${Math.round(elev)} m`);
  }
  const slope =
    sed.siteProfile?.data?.slopeMethod === 'unavailable'
      ? undefined
      : sed.siteProfile?.data?.slopePercent;
  if (typeof slope === 'number' && Number.isFinite(slope)) {
    parts.push(`${slope.toFixed(1)}% slope`);
  }
  const zoning = sed.parcel?.data?.zoning;
  if (zoning && typeof zoning === 'string' && zoning.length > 0) {
    parts.push(`zoning ${zoning}`);
  }
  if (parts.length === 0) return 'Siting data pending re-run.';
  return `Site siting characteristics: ${parts.join('; ')}. Detailed narrative pending re-run.`;
}

/**
 * Climate implications fallback — neutral data-grounded statement of
 * peak/avg temperature and humidity, no editorial. Fires when the
 * `climateSevereWeather.implications` field is instruction-shape.
 */
export function climateImplicationsFallback(site: SiteEntity, sed: SiteEvidenceDocument): string {
  const narrative = composeClimateSummaryFromSed(sed, {
    siteName: site.name,
    address: sed.location?.address ?? site.address,
  });
  if (narrative) return `${narrative} Detailed narrative pending re-run.`;
  return 'Climate analysis pending re-run.';
}

/**
 * Electric-grid infrastructure fallback. Composes from HIFLD grid evidence.
 */
export function electricGridFallback(_site: SiteEntity, sed: SiteEvidenceDocument): string {
  return formatVerifiedGridSummary(sed.grid);
}

/**
 * Transport-fiber infrastructure fallback. Composes from connectivity evidence.
 */
export function transportFiberFallback(_site: SiteEntity, sed: SiteEvidenceDocument): string {
  const ix = formatAttributedInternetExchangeFromSed(sed.connectivity);
  const cn = sed.connectivity?.data;
  const facility =
    cn && isVerifiedEvidenceSection(sed.connectivity)
      ? formatAttributedDatacenterFacilities(
          cn,
          resolveEvidenceSourceLabel(sed.connectivity, 'PeeringDB'),
        )
      : null;
  return [ix, facility].filter(Boolean).join(' ');
}

/**
 * Development-risk reason fallback. Generic deterministic placeholder used
 * when a specific risk entry's reason field is instruction-shape.
 */
export function developmentRiskReasonFallback(_site: SiteEntity, _sed: SiteEvidenceDocument): string {
  return 'Risk rationale pending re-run.';
}

/**
 * Recommendation description fallback. Same shape contract as risk reason.
 */
export function recommendationDescriptionFallback(_site: SiteEntity, _sed: SiteEvidenceDocument): string {
  return 'Recommendation detail pending re-run.';
}
